import React, { type JSX } from 'react';
import {
    Container,
    List,
    Stack,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
    Button,
    useTheme,
} from '@mui/material';
import { InfoListItem } from '@brightlayer-ui/react-components';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { CustomAppBar, CustomTransComponent } from 'components';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../../components/CustomIcon';
import { useLocation, useParams } from 'react-router-dom';
import {
    useGetDeviceByIdQuery,
    useGetSettingsQuery,
    useUpdateSettingsMutation,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { useDispatch } from 'react-redux';
import { useRBAC, useNewCustomTranslations } from 'hooks';
import { useTypedSelector } from '@fiji/common';
import {
    setAppHeaderSubTitle,
    setAppHeaderTitle,
    setMessageContent,
} from '@fiji/common/src/features/common/commonSlice';
import { SettingsRightComponent } from './SettingsRightComponent';
import { useDebounce, useIconLibraryFlag } from '@fiji/common/src/hooks';

export const Settings = (): JSX.Element => {
    useNewCustomTranslations('SETTINGS', false);
    const { state } = useLocation();
    const { deviceId } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isIconLibraryEnabled } = useIconLibraryFlag();
    const isEmpty = (val: any): boolean => val === undefined || val === null || val === '';
    const [searchKey, setSearchKey] = React.useState<any>(undefined);
    const [, debouncedValue] = useDebounce(undefined, 300, searchKey);
    const theme: any = useTheme();
    const [expandedCategories, setExpandedCategories] = React.useState<{ [key: string]: boolean }>({});

    const { data: deviceData } = useGetDeviceByIdQuery(deviceId, {
        skip: !deviceId,
    });
    const {
        data: settingsData,
        isLoading,
        isFetching,
    } = useGetSettingsQuery({ deviceId, debouncedValue }, { skip: !deviceId, refetchOnMountOrArgChange: true });

    const [updateDeviceSettings, { isLoading: updateLoader }] = useUpdateSettingsMutation();

    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateDevice = hasPermission('device-settings_edit-devices');

    const [payload, setPayload] = React.useState<any>({});

    React.useEffect(() => {
        dispatch(setAppHeaderTitle(state?.widgetName));
        dispatch(setAppHeaderSubTitle(deviceData?.data?.name));
        return () => {
            dispatch(setAppHeaderTitle(undefined));
            dispatch(setAppHeaderSubTitle(undefined));
        };
    }, [deviceData]);

    React.useEffect(() => {
        if (settingsData?.data && Object.keys(settingsData.data).length) setDefaultSettings();
    }, [settingsData]);

    const isSaveDisable = (): boolean => {
        let bool = true;
        if (settingsData?.data && Object.keys(settingsData.data).length) {
            Object.keys(settingsData.data).forEach((category) => {
                settingsData.data[category].forEach((item: any) => {
                    if (
                        !(isEmpty(item?.currentValue) && isEmpty(payload[item?.channelId])) &&
                        item?.currentValue !== payload[item?.channelId]
                    ) {
                        bool = false;
                    }
                });
            });
        }
        return bool;
    };

    const setDefaultSettings = (): void => {
        const requiredObject: any = {};
        Object.keys(settingsData.data).forEach((category) => {
            settingsData.data[category].forEach((item: any) => {
                requiredObject[item?.channelId] = item?.currentValue;
            });
        });
        setPayload(requiredObject);
    };

    const updateSettings = async (): Promise<void> => {
        const payloadClone: any = [];
        for (const key in payload) {
            const selectedIndex = payloadClone?.findIndex((item: any) => item?.channelId === key);
            let selectedSettingsData;
            if (settingsData?.data && typeof settingsData.data === 'object') {
                Object.values(settingsData.data).forEach((arr: any) => {
                    if (Array.isArray(arr)) {
                        const found = arr.find((item: any) => item?.channelId === key);
                        if (found) {
                            selectedSettingsData = found;
                            if (selectedSettingsData?.currentValue !== payload[key]) {
                                if (selectedIndex === -1) {
                                    payloadClone.push({ channelId: key, tagValue: payload[key] });
                                } else {
                                    payloadClone[selectedIndex].value = payload[key];
                                }
                            }
                        }
                    }
                });
            }
        }
        const { error }: any = await updateDeviceSettings({ body: payloadClone, deviceId });
        if (!error) {
            dispatch(setMessageContent({ isOpen: true, message: t('SETTINGS:SETTINGS_UPDATED_SUCCESSFULLY') }));
        } else {
            setDefaultSettings();
        }
    };

    React.useEffect(() => {
        if (settingsData?.data && Object?.keys(settingsData?.data)?.length) {
            const requiredCategories: { [key: string]: boolean } = {};
            Object.keys(settingsData?.data)?.forEach((category: any) => {
                requiredCategories[category] = true;
            });
            setExpandedCategories(requiredCategories);
        }
    }, [settingsData]);

    const getSwitchContent = (item: any): any => {
        if (item?.dataType === 'boolean') {
            return 'toggle';
        }
        return 'text';
    };

    const handleAllExpandCollapse = (): void => {
        const isExpanded = Object?.values(expandedCategories)?.every((item: any) => item === true);
        const expandedCategoriesClone = { ...expandedCategories };
        for (const key in expandedCategoriesClone) {
            if (isExpanded) {
                expandedCategoriesClone[key] = false;
            } else {
                expandedCategoriesClone[key] = true;
            }
        }
        setExpandedCategories(expandedCategoriesClone);
    };

    const renderPropertiesAccordions = (item: any): JSX.Element => (
        <Accordion
            key={item}
            expanded={expandedCategories[item] ?? true}
            onChange={(): void => {
                setExpandedCategories((prev: any) => ({ ...prev, [item]: !prev[item] }));
            }}
            defaultExpanded
        >
            <AccordionSummary
                expandIcon={<ExpandMore color="primary" />}
                aria-controls="panel1bh-content"
                id="expand"
                className="border-bottom-1"
            >
                <Typography
                    variant="body2"
                    fontWeight={'600'}
                    fontSize={'14px'}
                    sx={{ color: theme.palette?.primary?.main }}
                >
                    {item ?? '--'}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className="padding-0">
                <List disablePadding>
                    {settingsData?.data[item]?.map((setting: any) => {
                        const { name, iconLibrary } = setting?.icon ?? {};
                        return (
                            <InfoListItem
                                key={setting?.id}
                                className="border-bottom-1"
                                icon={
                                    <CustomIcon
                                        family={
                                            setting?.icon?.web?.family ??
                                            (isIconLibraryEnabled ? iconLibrary?.name : undefined)
                                        }
                                        iconName={setting?.icon?.web?.name ?? (isIconLibraryEnabled ? name : undefined)}
                                    />
                                }
                                title={setting?.displayName}
                                {...(getSwitchContent(setting) === 'text' && { sx: { height: '96px' } })}
                                rightComponent={
                                    <SettingsRightComponent
                                        item={setting}
                                        type={setting?.values?.length ? 'select' : getSwitchContent(setting)}
                                        payload={payload}
                                        setPayload={setPayload}
                                    />
                                }
                            />
                        );
                    })}
                </List>
            </AccordionDetails>
        </Accordion>
    );

    const getAccordionContent = (): any => {
        if (isLoading || isFetching) {
            return (
                <Accordion defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ExpandMore color="primary" />}
                        aria-controls="panel1bh-content"
                        id="expand"
                        className="border-bottom-1"
                    >
                        <Skeleton animation="wave">
                            <Typography
                                variant="body2"
                                fontWeight={'600'}
                                fontSize={'14px'}
                                sx={{ color: theme.palette?.primary?.main }}
                            >
                                <CustomTransComponent translationKey={'COMMON:PRIORITY'} />
                            </Typography>
                        </Skeleton>
                    </AccordionSummary>
                    <AccordionDetails className="padding-0">
                        <ListSkeleton total={10} customSize={25} customSx={{ height: '4.5rem' }} />
                    </AccordionDetails>
                </Accordion>
            );
        } else if (Object.keys(settingsData?.data ?? {})?.length) {
            return Object.keys(settingsData?.data)?.map((item: any) => renderPropertiesAccordions(item));
        }
        return (
            <CustomEmptyState icon title={<CustomTransComponent translationKey={'COMMON:NO_SETTINGS_AVAILABLE'} />} />
        );
    };

    return (
        <>
            {canUpdateDevice && (
                <CustomAppBar
                    isSaveButtonDisabled={isSaveDisable() || updateLoader}
                    isDiscardButtonDisabled={isSaveDisable() || updateLoader}
                    handleDiscard={setDefaultSettings}
                    handleSubmit={updateSettings}
                />
            )}
            <Container className="padding-y-36">
                <Stack mb={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Stack direction={'row'} alignItems={'center'} spacing={3}>
                        <InputWithCustomPlaceholder
                            id="searchKey"
                            value={searchKey ?? ''}
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                setSearchKey(e.target.value);
                            }}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Button
                            id="toggleExpand"
                            sx={{
                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.[50],
                                },
                            }}
                            variant="outlined"
                            onClick={handleAllExpandCollapse}
                            startIcon={<UnfoldMore />}
                        >
                            {Object?.values(expandedCategories)?.every((item: any) => item === true) ? (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:COLLAPSE_ALL'} />
                            ) : (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:EXPAND_ALL'} />
                            )}
                        </Button>
                    </Stack>
                </Stack>

                <Stack>{getAccordionContent()}</Stack>
            </Container>
        </>
    );
};
